'use client';

import type { CSSProperties } from 'react';
import { motion, useTransform, type MotionStyle } from 'motion/react';
import styles from './Compass.module.css';
import { FieldOfView } from './FieldOfView/FieldOfView';
import { Tick } from './Tick/Tick';
import { useCompassInput } from './useCompassInput';
import { useCompassScale } from './useCompassScale';
import { useCompassSky } from './useCompassSky';
import { useCompassSound } from './useCompassSound';

const BUFFER_TICKS = 3;

export const Compass = () => {
  const { compassRef, attachCompass, position, activeTick } = useCompassInput();
  const { visibleTicks, ticksPerTurn, tickSpacing } =
    useCompassScale(compassRef);
  const bearing = useTransform(
    () => 270 + (position.get() / ticksPerTurn) * 360
  );
  useCompassSky(bearing);
  useCompassSound(activeTick);

  const firstTick = activeTick - Math.floor(visibleTicks / 2) - BUFFER_TICKS;
  const ticks = Array.from(
    { length: visibleTicks + BUFFER_TICKS * 2 },
    (_, index) => firstTick + index
  );

  return (
    <motion.div
      ref={attachCompass}
      className={styles.compass}
      style={
        {
          '--offset': position,
          '--tick-spacing': `${tickSpacing}px`,
        } as MotionStyle
      }
    >
      <div
        className={styles.ticks}
        style={{ '--first-index': firstTick } as CSSProperties}
      >
        {ticks.map((index) => (
          <Tick
            key={index}
            index={index}
            active={index === activeTick}
            ticksPerTurn={ticksPerTurn}
          />
        ))}
      </div>

      <FieldOfView
        bearing={bearing}
        ticksPerTurn={ticksPerTurn}
        tickSpacing={tickSpacing}
      />
      <div className={styles.indicator} />
    </motion.div>
  );
};
